import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Lightbulb, InboxIcon, Search, Pin } from 'lucide-react';
import { getMasterMemory } from '../data';
import './AgentLogs.css';

const TABS = [
  { key: 'all', label: 'All Logs' },
  { key: 'skill', label: 'Skill Logs' },
  { key: 'listener', label: 'Listener Logs' },
];

const LEVEL_CONFIG = {
  error: { color: '#ef4444', bg: 'rgba(239,68,68,0.12)', label: 'ERROR', icon: '✕' },
  warn: { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', label: 'WARN', icon: '⚠' },
  success: { color: '#10b981', bg: 'rgba(16,185,129,0.12)', label: 'OK', icon: '✓' },
  info: { color: '#64748b', bg: 'transparent', label: 'INFO', icon: '›' },
  debug: { color: '#475569', bg: 'transparent', label: 'DEBUG', icon: '·' },
};

const EVENT_ICONS = {
  heartbeat_fired: 'HB',
  cron_fired: 'CR',
  poll_processed: 'PL',
  webhook_processed: 'WH',
  webhook_push_processed: 'WP',
  skill_exec: 'SK',
  skill_browse: 'SB',
  end_interaction: 'OK',
  interaction_ended: 'OK',
  event_received: 'EV',
  agent_action: 'AG',
  escalation: 'ES',
  user_log: 'UL',
};

function formatTs(ts) {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return ts;
    return d.toLocaleString('en-US', {
      month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false
    });
  } catch { return ts; }
}

function relativeTime(ts) {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    const diff = Date.now() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  } catch { return ''; }
}

export default function AgentLogs() {
  const { agent } = useOutletContext();
  const [activeTab, setActiveTab] = useState('all');
  const [logs, setLogs] = useState([]);
  const [listeners, setListeners] = useState([]);
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSource, setSelectedSource] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');
  const [showTip, setShowTip] = useState(true);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [autoScroll, setAutoScroll] = useState(true);
  const [pollActive, setPollActive] = useState(true);
  const containerRef = useRef(null);

  const agentId = agent?.id;

  const fetchLogs = useCallback(async () => {
    if (!agentId) return;
    try {
      const rawEntries = await getMasterMemory(agentId, 500);
      const processed = [];
      const seenSkills = new Set();
      const listenerSet = new Set();

      for (const entry of rawEntries) {
        const eventType = entry.event_type || '';
        const source = entry.source || '';
        const ts = entry.timestamp || entry.created_at || '';

        let level = 'info';
        if (String(entry.summary || '').toLowerCase().includes('error') || String(entry.outcome || '').toLowerCase().includes('error')) level = 'error';
        else if (String(entry.summary || '').toLowerCase().includes('warning') || String(entry.outcome || '').toLowerCase().includes('fail')) level = 'warn';
        else if (['end_interaction', 'interaction_ended'].includes(eventType)) level = 'success';
        if (eventType === 'user_log') level = entry.log_level || 'info';

        const isSkill = eventType === 'skill_exec' || eventType === 'skill_browse' || (eventType === 'user_log' && entry.source_type === 'skill');
        const isListener = ['poll_processed', 'webhook_processed', 'webhook_push_processed', 'cron_fired', 'heartbeat_fired'].includes(eventType) || (eventType === 'user_log' && entry.source_type === 'listener');

        if (activeTab === 'skill' && !isSkill) continue;
        if (activeTab === 'listener' && !isListener) continue;

        if (selectedSource) {
          const threadId = entry.thread_id || '';
          const skillId = entry.skill_id || '';
          if (source !== selectedSource && threadId !== selectedSource && !threadId.endsWith(`_${selectedSource}`) && skillId !== selectedSource) continue;
        }

        if (isSkill) {
          const sname = entry.skill_id || source;
          if (sname && !seenSkills.has(sname)) {
            seenSkills.add(sname);
          }
        }
        if (isListener && source) listenerSet.add(source);

        const details = {};
        for (const k of ['action_type', 'action_payload', 'skill_id', 'status', 'result', 'metadata', 'action_details']) {
          if (entry[k] !== undefined) details[k] = entry[k];
        }

        processed.push({
          id: entry._id || '',
          timestamp: ts,
          event_type: eventType,
          source,
          thread_id: entry.thread_id || '',
          summary: entry.summary || '',
          outcome: entry.outcome || '',
          level,
          category: isSkill ? 'skill' : (isListener ? 'listener' : 'system'),
          details
        });
      }

      setLogs(processed);
      setSkills([...seenSkills].map(name => ({ name })));
      setListeners([...listenerSet].map(id => ({ id, name: id, type: 'polling' })));
    } catch (e) {
      console.error('Failed to fetch logs:', e);
    } finally {
      setLoading(false);
    }
  }, [agentId, activeTab, selectedSource]);

  useEffect(() => {
    setLoading(true);
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    if (!pollActive) return;
    const interval = setInterval(fetchLogs, 8000);
    return () => clearInterval(interval);
  }, [fetchLogs, pollActive]);

  useEffect(() => {
    // Scroll the terminal itself rather than scrollIntoView() — the latter walks
    // up and scrolls every ancestor container, which can drag the top nav out of view.
    if (autoScroll && containerRef.current) {
      containerRef.current.scrollTo({ top: containerRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [logs, autoScroll]);

  const handleScroll = () => {
    if (!containerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    setAutoScroll(scrollHeight - scrollTop - clientHeight < 80);
  };

  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const filteredLogs = useMemo(() => {
    // The backend hands master memory back newest-first (see
    // sandbox._read_master_memory + the reverse() it does before returning).
    // We want a `tail -f` feel here: oldest at the top, newest at the bottom,
    // so the auto-scroll-to-bottom keeps you pinned to fresh entries. Reverse
    // once at the render layer instead of changing the API contract.
    const out = [];
    for (let i = logs.length - 1; i >= 0; i--) {
      const log = logs[i];
      if (levelFilter !== 'all' && log.level !== levelFilter) continue;
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const haystack = `${log.summary} ${log.source} ${log.outcome} ${log.event_type}`.toLowerCase();
        if (!haystack.includes(term)) continue;
      }
      out.push(log);
    }
    return out;
  }, [logs, levelFilter, searchTerm]);

  const stats = useMemo(() => {
    const s = { total: filteredLogs.length, error: 0, warn: 0, success: 0, info: 0 };
    filteredLogs.forEach(l => { s[l.level] = (s[l.level] || 0) + 1; });
    return s;
  }, [filteredLogs]);

  return (
    <div className="logs-page">
      <div className="logs-header">
        <div className="logs-title-row">
          <h2>Logs</h2>
          <div className="logs-stats-bar">
            <span className="logs-stat total">{stats.total} entries</span>
            {stats.error > 0 && <span className="logs-stat error">{stats.error} errors</span>}
            {stats.warn > 0 && <span className="logs-stat warn">{stats.warn} warnings</span>}
            <span className="logs-stat success">{stats.success} ok</span>
          </div>
        </div>

        <div className="logs-tabs">
          {TABS.map(tab => (
            <button
              key={tab.key}
              className={`logs-tab ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => { setActiveTab(tab.key); setSelectedSource(''); }}
            >
              
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="logs-toolbar">
        {activeTab === 'skill' && skills.length > 0 && (
          <select
            className="logs-source-select"
            value={selectedSource}
            onChange={e => setSelectedSource(e.target.value)}
          >
            <option value="">All Skills</option>
            {skills.map(s => (
              <option key={s.name} value={s.name}>{s.name}</option>
            ))}
          </select>
        )}
        {activeTab === 'listener' && listeners.length > 0 && (
          <select
            className="logs-source-select"
            value={selectedSource}
            onChange={e => setSelectedSource(e.target.value)}
          >
            <option value="">All Listeners</option>
            {listeners.map(l => (
              <option key={l.id} value={l.id}>
                {l.name} ({l.type})
              </option>
            ))}
          </select>
        )}

        <div className="logs-search-wrapper">
          <span className="logs-search-icon"><Search size={14} /></span>
          <input
            type="text"
            className="logs-search"
            placeholder="Search logs..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="logs-search-clear" onClick={() => setSearchTerm('')}>✕</button>
          )}
        </div>

        <div className="logs-level-filters">
          <button className={`logs-level-btn ${levelFilter === 'all' ? 'active' : ''}`} onClick={() => setLevelFilter('all')}>All</button>
          <button className={`logs-level-btn error ${levelFilter === 'error' ? 'active' : ''}`} onClick={() => setLevelFilter('error')}>Errors</button>
          <button className={`logs-level-btn warn ${levelFilter === 'warn' ? 'active' : ''}`} onClick={() => setLevelFilter('warn')}>Warnings</button>
          <button className={`logs-level-btn success ${levelFilter === 'success' ? 'active' : ''}`} onClick={() => setLevelFilter('success')}>Success</button>
        </div>

        <div className="logs-toolbar-right">
          <button
            className={`logs-toggle-btn ${pollActive ? 'active' : ''}`}
            onClick={() => setPollActive(!pollActive)}
            title={pollActive ? 'Pause live updates' : 'Resume live updates'}
          >
            {pollActive ? '⏸ Live' : '▶ Paused'}
          </button>
          <button
            className={`logs-toggle-btn ${autoScroll ? 'active' : ''}`}
            onClick={() => setAutoScroll(!autoScroll)}
            title={autoScroll ? 'Disable auto-scroll' : 'Enable auto-scroll'}
          >
            {autoScroll ? '↓ Auto' : '↓ Manual'}
          </button>
        </div>
      </div>

      {showTip && (
        <div className="logs-tip">
          <div className="logs-tip-content">
            <span className="logs-tip-icon"><Lightbulb size={16} /></span>
            <div className="logs-tip-text">
              <strong>Debug with agent.log()</strong> — Use <code>agent.log("your message")</code> anywhere in your listener or skill Python code to print custom debug messages here. Also available: <code>agent.error()</code>, <code>agent.warn()</code>, <code>agent.info()</code>, and <code>agent.debug()</code>.
            </div>
            <button className="logs-tip-close" onClick={() => setShowTip(false)}>✕</button>
          </div>
        </div>
      )}

      <div className="logs-terminal" ref={containerRef} onScroll={handleScroll}>
        {loading ? (
          <div className="logs-empty">
            <div className="logs-spinner" />
            <p>Loading logs...</p>
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="logs-empty">
            <span className="logs-empty-icon"><InboxIcon size={32} /></span>
            <p>No logs found</p>
            <span className="logs-empty-hint">
              {searchTerm ? 'Try a different search term' : 'Logs will appear here as your agent runs'}
            </span>
          </div>
        ) : (
          <div className="logs-entries">
            <AnimatePresence initial={false}>
              {filteredLogs.map((log, idx) => {
                const lc = LEVEL_CONFIG[log.level] || LEVEL_CONFIG.info;
                const evIcon = EVENT_ICONS[log.event_type] || '--';
                const isExpanded = expandedIds.has(log.id);
                const hasDetails = log.details && Object.keys(log.details).length > 0;

                return (
                  <motion.div
                    key={log.id || idx}
                    className={`log-entry level-${log.level}${log.event_type === 'user_log' ? ' user-log' : ''}`}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    style={{ backgroundColor: lc.bg }}
                    onClick={() => hasDetails && toggleExpand(log.id)}
                  >
                    <div className="log-line">
                      <span className="log-timestamp">{formatTs(log.timestamp)}</span>
                      <span className="log-level-badge" style={{ color: lc.color }}>{lc.icon} {lc.label}</span>
                      <span className="log-event-icon">{evIcon}</span>
                      <span className="log-source">{log.source || log.event_type}</span>
                      <span className="log-summary">{log.summary}</span>
                      {log.outcome && <span className="log-outcome">{log.outcome}</span>}
                      <span className="log-relative">{relativeTime(log.timestamp)}</span>
                      {hasDetails && (
                        <span className={`log-expand-icon ${isExpanded ? 'expanded' : ''}`}>▸</span>
                      )}
                    </div>

                    <AnimatePresence>
                      {isExpanded && hasDetails && (
                        <motion.div
                          className="log-details"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <pre className="log-details-content">
                            {JSON.stringify(log.details, null, 2)}
                          </pre>
                          {log.thread_id && (
                            <div className="log-thread-id">Thread: {log.thread_id}</div>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
}
